import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminElearning = ({ onNavigate, userInfo }) => {
    const [playlists, setPlaylists] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDept, setSelectedDept] = useState('all');
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editPlaylist, setEditPlaylist] = useState(null);
    const [form, setForm] = useState({ name: '', department: '', subject: '', teacher_id: '', teacher_name: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [stats, setStats] = useState({ total_playlists: 0, total_videos: 0, total_notes: 0 });

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchDepartments(); }, []);
    useEffect(() => { fetchPlaylists(); }, [selectedDept, search]);

    const fetchDepartments = async () => {
        try {
            const res = await fetch(`${API_LINK}/api/admin/departments`, { headers });
            const r = await res.json();
            if (r.success) setDepartments(r.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchPlaylists = async () => {
        try {
            setLoading(true);
            let url = `${API_LINK}/api/elearning/admin/playlists?department=${selectedDept || ''}`;
            if (search.trim()) url += `&search=${encodeURIComponent(search)}`;
            const res = await fetch(url, { headers });
            const r = await res.json();
            if (r.success) {
                setPlaylists(r.data || []);
                if (r.stats) setStats(r.stats);
                else {
                    // Fallback local calc if stars not sent (backwards compat)
                    setStats({
                       total_playlists: (r.data || []).length,
                       total_videos: (r.data || []).reduce((a, p) => a + (p.video_count || 0), 0),
                       total_notes: (r.data || []).reduce((a, p) => a + (p.note_count || 0), 0)
                    });
                }
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const fetchTeachers = async (dept) => {
        try {
            const res = await fetch(`${API_LINK}/api/admin/teachers-by-department?department=${encodeURIComponent(dept)}`, { headers });
            const r = await res.json();
            if (r.success) setTeachers(r.data || []);
        } catch (e) { console.error(e); }
    };



    const openAddModal = () => {
        alert('Subjects are automatically managed. Use the Edit icon on a subject to assign a teacher.');
    };

    const openEditModal = (p) => {
        setEditPlaylist(p);
        setForm({ 
            name: p.name, 
            department: p.department, 
            subject: p.subject, 
            teacher_id: p.teacher_id || '', 
            teacher_name: p.teacher_name || '', 
            description: p.description || '' 
        });
        if (p.department) fetchTeachers(p.department);
        setShowModal(true);
    };

    const handleDeptChange = (dept) => {
        setForm(f => ({ ...f, department: dept, teacher_id: '', teacher_name: '' }));
        if (dept) fetchTeachers(dept);
        else setTeachers([]);
    };

    const handleTeacherSelect = (tid) => {
        const t = teachers.find(x => String(x.id) === String(tid));
        setForm(f => ({ ...f, teacher_id: tid, teacher_name: t ? t.name : '' }));
    };

    const handleSave = async () => {
        if (!form.teacher_id) return alert('Please select a teacher to assign');
        setSaving(true);
        try {
            const url = `${API_LINK}/api/elearning/admin/playlists/assign`;
            const res = await fetch(url, { 
                method: 'POST', 
                headers, 
                body: JSON.stringify({
                    subject_id: editPlaylist.id,
                    teacher_id: form.teacher_id
                }) 
            });
            const r = await res.json();
            if (r.success) { 
                setShowModal(false); 
                fetchPlaylists(); 
                alert('Teacher assigned successfully!');
            }
            else alert(r.message || 'Failed');
        } catch (e) { alert('Error saving assignment'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove the teacher assignment from this subject?')) return;
        try {
            const res = await fetch(`${API_LINK}/api/elearning/admin/playlists/${id}/assign`, { 
                method: 'DELETE', 
                headers 
            });
            const r = await res.json();
            if (r.success) { fetchPlaylists(); }
            else alert(r.message || 'Failed');
        } catch (e) { alert('Error unassigning'); }
    };

    const cs = {
        card: { background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        btn: { padding: '0.6rem 1.2rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' },
        input: { width: '100%', padding: '0.65rem 1rem', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
        label: { display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-elearning" userInfo={userInfo}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#1a1a2e' }}>📚 E-Learning Management</h1>
                    <p style={{ margin: '0.3rem 0 0', color: '#666', fontSize: '0.95rem' }}>Create playlists, assign teachers, manage content</p>
                </div>
                <button onClick={openAddModal} style={{ ...cs.btn, background: '#238548', color: '#fff', padding: '0.7rem 1.5rem', fontSize: '1rem' }}>
                    ➕ Create Playlist
                </button>
            </div>

            {/* Filters */}
            <div style={{ ...cs.card, marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={cs.label}>🏛️ Department</label>
                    <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)} style={cs.input}>
                        <option value="all">All Departments</option>
                        {departments.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                    </select>
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={cs.label}>🔍 Search</label>
                    <input placeholder="Search playlists..." value={search} onChange={e => setSearch(e.target.value)} style={cs.input} />
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Playlists', value: stats.total_playlists, color: '#238548', icon: '📚' },
                    { label: 'Total Videos', value: stats.total_videos, color: '#2196F3', icon: '🎥' },
                    { label: 'Total Notes', value: stats.total_notes, color: '#FF9800', icon: '📝' }
                ].map((s, i) => (
                    <div key={i} style={{ ...cs.card, flex: '1', minWidth: '160px', borderLeft: `4px solid ${s.color}` }}>
                        <div style={{ color: '#888', fontSize: '0.85rem' }}>{s.icon} {s.label}</div>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: s.color }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Playlist List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                    <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #238548', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                    Loading playlists...
                </div>
            ) : playlists.length === 0 ? (
                <div style={{ ...cs.card, textAlign: 'center', padding: '3rem', color: '#888' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: 0, color: '#555' }}>No Playlists Found</h3>
                    <p style={{ margin: '0.5rem 0 0' }}>Create your first playlist to get started.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.2rem' }}>
                    {playlists.map(p => (
                        <div key={p.id} style={{ ...cs.card, display: 'flex', flexDirection: 'column', gap: '0.8rem', border: '2px solid transparent', transition: 'all 0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.border = '2px solid #238548'}
                            onMouseLeave={e => e.currentTarget.style.border = '2px solid transparent'}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1a1a2e' }}>{p.name}</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                                        <span style={{ background: '#e8f5e9', color: '#238548', padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>{p.department}</span>
                                        <span style={{ background: '#e3f2fd', color: '#2196F3', padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>{p.subject}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.3rem' }}>
                                    <button onClick={() => openEditModal(p)} style={{ ...cs.btn, background: '#e3f2fd', color: '#2196F3', padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>✏️</button>
                                    <button onClick={() => setDeleteConfirm(p.id)} style={{ ...cs.btn, background: '#ffebee', color: '#f44336', padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>🗑️</button>
                                </div>
                            </div>

                            {p.description && <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: 1.4 }}>{p.description}</p>}

                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '0.85rem', color: '#555' }}>👨‍🏫 <strong>{p.teacher_name || 'Unassigned'}</strong></div>
                                <div style={{ fontSize: '0.8rem', color: '#888' }}>🎥 {p.video_count || 0} videos</div>
                                <div style={{ fontSize: '0.8rem', color: '#888' }}>📝 {p.note_count || 0} notes</div>
                            </div>

                            <div style={{ fontSize: '0.75rem', color: '#aaa' }}>Created: {new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', width: '95%', maxWidth: '550px', maxHeight: '90vh', overflow: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#1a1a2e' }}>{editPlaylist ? '✏️ Edit Playlist' : '➕ Create Playlist'}</h2>
                            <button onClick={() => setShowModal(false)} style={{ ...cs.btn, background: '#f5f5f5', color: '#666', padding: '0.3rem 0.7rem' }}>✕</button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={cs.label}>Playlist Name *</label>
                                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Data Structures Lectures" style={cs.input} />
                            </div>

                            <div>
                                <label style={cs.label}>Department *</label>
                                <select value={form.department} onChange={e => handleDeptChange(e.target.value)} style={cs.input}>
                                    <option value="">Select Department</option>
                                    {departments.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                                </select>
                            </div>

                            <div>
                                <label style={cs.label}>Subject *</label>
                                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Computer Science" style={cs.input} />
                            </div>

                            <div>
                                <label style={cs.label}>Assign Teacher</label>
                                <select value={form.teacher_id} onChange={e => handleTeacherSelect(e.target.value)} style={cs.input}>
                                    <option value="">Select Teacher</option>
                                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name} — {t.designation || t.department}</option>)}
                                </select>
                            </div>

                            <div>
                                <label style={cs.label}>Description</label>
                                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description..." rows={3} style={{ ...cs.input, resize: 'vertical' }} />
                            </div>

                            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                                <button onClick={() => setShowModal(false)} style={{ ...cs.btn, background: '#f5f5f5', color: '#666' }}>Cancel</button>
                                <button onClick={handleSave} disabled={saving} style={{ ...cs.btn, background: '#238548', color: '#fff', opacity: saving ? 0.7 : 1 }}>
                                    {saving ? '⏳ Saving...' : editPlaylist ? '💾 Update' : '➕ Create'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', width: '90%', maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                        <h3 style={{ margin: '0 0 0.5rem' }}>Delete Playlist?</h3>
                        <p style={{ color: '#666', marginBottom: '1.5rem' }}>This will also delete all videos and notes in this playlist. This cannot be undone.</p>
                        <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center' }}>
                            <button onClick={() => setDeleteConfirm(null)} style={{ ...cs.btn, background: '#f5f5f5', color: '#666' }}>Cancel</button>
                            <button onClick={() => handleDelete(deleteConfirm)} style={{ ...cs.btn, background: '#f44336', color: '#fff' }}>🗑️ Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </Layout>
    );
};

export default AdminElearning;
